import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { BillJobAllocation } from '../components/JobCosting';
import { useAuth } from '../context/AuthContext';
const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

const Badge = ({ status }) => {
  const cls = status==='paid'?'b-paid':status==='part_paid'?'b-part':'b-unpaid';
  return <span className={'badge '+cls}>{(status||'').replace('_',' ')}</span>;
};

export default function Bills() {
  const { hasPermission } = useAuth();
  const [jobOrders, setJobOrders] = React.useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [modal, setModal] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [allocateModal, setAllocateModal] = useState(null);

  const load = () => {
    setLoading(true);
    get('/bills').then(d => setBills(d.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); get('/suppliers').then(d => setSuppliers(d.data)); }, []);

  return (
    <div>
      <div className="flex-between mb-lg">
        <h2 className="page-title">Bills & Expenses</h2>
        {hasPermission('bills:write') && (
          <button className="btn btn-primary" onClick={() => setModal({})}>+ Add Bill</button>
        )}
      </div>
      <div className="tbl-wrap">
        {loading ? <div className="spinner"></div> : (
          <table>
            <thead><tr>
              <th>Bill #</th><th>Date</th><th>Supplier</th>
              <th style={{textAlign:'right'}}>Amount</th>
              <th style={{textAlign:'right'}}>Paid</th>
              <th style={{textAlign:'right'}}>Balance</th>
              <th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {bills.length === 0
                ? <tr><td colSpan="8"><div className="empty-state"><h3>No bills yet</h3></div></td></tr>
                : bills.map(b => (
                  <tr key={b.id}>
                    <td style={{fontWeight:500}}>{b.bill_number}</td>
                    <td>{b.date}</td><td>{b.supplier_name||b.category||'—'}</td>
                    <td className="amt">{fmt(b.amount)}</td>
                    <td className="amt">{fmt(b.amount_paid)}</td>
                    <td className="amt" style={{color:b.balance_due>0?'var(--unpaid)':'var(--paid)'}}>
                      {fmt(b.balance_due)}
                    </td>
                    <td><Badge status={b.status} /></td>
                    <td>
                      <div className="flex-center gap-sm">
                        {hasPermission('payments:write') && b.status!=='paid' && (
                          <button className="btn btn-gold btn-sm" onClick={() => setPayModal(b)}>Pay</button>
                        )}
                        {hasPermission('bills:write') && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditModal(b)}>Edit</button>
                        )}
                        {hasPermission('bills:delete') && (
                          <button className="btn btn-danger btn-sm" onClick={async () => {
                            if (!window.confirm('Delete this bill?')) return;
                            try { await del('/bills/'+b.id); load(); }
                            catch (err) { alert(err.message); }
                          }}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>
      {modal !== null && (
        <BillModal suppliers={suppliers} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {allocateModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-hdr">
              <span className="modal-title">Link to Job Orders — {allocateModal.bill_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setAllocateModal(null)}>X</button>
            </div>
            <div className="modal-body">
              <BillJobAllocation
                billId={allocateModal.id}
                billAmount={allocateModal.amount}
                onSaved={() => setAllocateModal(null)} />
            </div>
          </div>
        </div>
      )}
      {editModal && (
        <EditBillModal bill={editModal} suppliers={suppliers}
          onClose={() => setEditModal(null)}
          onSaved={() => { setEditModal(null); load(); }} />
      )}
      {payModal && (
        <BillPayModal bill={payModal} onClose={() => setPayModal(null)} onSaved={() => { setPayModal(null); load(); }} />
      )}
    </div>
  );
}

function BillModal({ suppliers, onClose, onSaved }) {
  const [form, setForm] = useState({ supplier_id:'', date:new Date().toISOString().split('T')[0], due_date:'', category:'', description:'', amount:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await post('/bills', form); onSaved(); } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay"><div className="modal">
      <div className="modal-hdr">
        <span className="modal-title">New Bill / Expense</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">
        <div className="form-row">
          <div className="form-group"><label className="form-label">Supplier *</label>
            <select className="form-control" value={form.supplier_id} onChange={e=>setForm({...form,supplier_id:e.target.value})}>
              <option value="">Select supplier</option>
              {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Amount (N) *</label>
            <input type="number" className="form-control" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Date</label>
            <input type="date" className="form-control" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Due Date</label>
            <input type="date" className="form-control" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})} /></div>
        </div>
        <div className="form-group"><label className="form-label">Category</label>
          <input className="form-control" placeholder="e.g. Raw Materials, Printing, Transport" value={form.category} onChange={e=>setForm({...form,category:e.target.value})} /></div>
        <div className="form-group"><label className="form-label">Description</label>
          <textarea className="form-control" rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></div>
      </div>
      <div className="modal-ftr">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving?'Saving...':'Add Bill'}</button>
      </div>
    </div></div>
  );
}

function BillPayModal({ bill, onClose, onSaved }) {
  const [form, setForm] = useState({ date:new Date().toISOString().split('T')[0], amount:bill.balance_due, method:'transfer', reference:'', bank_account_id:'' });
  const [bankAccounts, setBankAccounts] = React.useState([]);
  React.useEffect(() => { get('/bank-accounts').then(d => setBankAccounts(d.data||[])).catch(()=>{}); }, []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setSaving(true);
    try { await post('/bills/'+bill.id+'/payments', form); onSaved(); }
    catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay"><div className="modal">
      <div className="modal-hdr">
        <span className="modal-title">Pay Bill — {bill.bill_number}</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">
        {error && <div className="alert alert-error mb-md">{error}</div>}
        <div style={{background:'var(--cream)',padding:12,borderRadius:8,marginBottom:16,fontSize:13}}>
          Balance due: <strong>{'₦'}{Number(bill.balance_due).toLocaleString()}</strong>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Date</label>
            <input type="date" className="form-control" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Amount</label>
            <input type="number" className="form-control" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Method</label>
            <select className="form-control" value={form.method} onChange={e=>setForm({...form,method:e.target.value})}>
              <option value="transfer">Transfer</option><option value="cash">Cash</option><option value="pos">POS</option>
            </select></div>
          <div className="form-group"><label className="form-label">Reference</label>
            <input className="form-control" value={form.reference} onChange={e=>setForm({...form,reference:e.target.value})} /></div>
        </div>
        {['transfer','pos'].includes(form.method) && (
          <div className="form-group">
            <label className="form-label">Bank Account *</label>
            <select className="form-control" value={form.bank_account_id||''} onChange={e=>setForm({...form,bank_account_id:e.target.value})}>
              <option value="">Select account...</option>
              {bankAccounts.filter(a=>a.type==='bank').map(a=>(
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="modal-ftr">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-gold" onClick={submit} disabled={saving}>{saving?'Saving...':'Record Payment'}</button>
      </div>
    </div></div>
  );
}

function EditBillModal({ bill, suppliers, onClose, onSaved }) {
  const [form, setForm] = React.useState({
    supplier_id: bill.supplier_id||'',
    date: bill.date||'',
    due_date: bill.due_date||'',
    category: bill.category||'',
    description: bill.description||'',
    amount: bill.amount||'',
    notes: bill.notes||''
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (!form.supplier_id || !form.amount) { setError('Supplier and amount required'); return; }
    setSaving(true);
    try {
      await put('/bills/'+bill.id, form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-hdr">
          <span className="modal-title">Edit Bill — {bill.bill_number}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Supplier *</label>
              <select className="form-control" value={form.supplier_id} onChange={e => setForm({...form,supplier_id:e.target.value})}>
                <option value="">Select supplier</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Amount (N) *</label>
              <input type="number" className="form-control" value={form.amount} onChange={e => setForm({...form,amount:e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={form.date} onChange={e => setForm({...form,date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input type="date" className="form-control" value={form.due_date||''} onChange={e => setForm({...form,due_date:e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Category</label>
            <input className="form-control" value={form.category||''} onChange={e => setForm({...form,category:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={2} value={form.description||''} onChange={e => setForm({...form,description:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes||''} onChange={e => setForm({...form,notes:e.target.value})} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
